import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { Box, Card, Chip, Fab } from '@mui/material';
import isFunction from 'lodash/isFunction';
import { ChangeEvent, useEffect, useState } from 'react';
import {
  Count,
  TabbedShowLayout,
  TabbedShowLayoutTabs,
  TabbedShowLayoutTabsProps,
  useRecordContext,
  useTranslate,
} from 'react-admin';

import { BOLogIdFieldMap } from '@Common/types/bo/bo_logs';

import { CommentsProps } from '@sh4res/bo-common/components/Comments/CommentsButton';
import { CommentsPanel } from '@sh4res/bo-common/components/Comments/CommentsPanel';
import { LogsPanel, LogsProps } from '@sh4res/bo-common/components/Logs/LogsButton';
import { Tab } from '@sh4res/bo-common/components/Tab';
import { useCanAccess } from '@sh4res/bo-common/hooks';

export type RightPanelTab = 'comments' | 'logs';

const Tabs = ({
  tab,
  onChange,
  comments,
  ...props
}: TabbedShowLayoutTabsProps & { tab: RightPanelTab; comments?: CommentsProps }) => {
  useEffect(() => {
    onChange?.(null as unknown as ChangeEvent<null>, tab === 'logs' && Boolean(comments) ? 1 : 0);
    // Tab acts as default tab or to programmatically change it.
    // react-admin does not memoize onChange, including it in dependencies breaks functionality
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  return <TabbedShowLayoutTabs onChange={onChange} {...props} />;
};

export function RightPanel({
  logs,
  comments,
  onClose,
  tab,
}: {
  logs?: LogsProps;
  comments?: CommentsProps;
  onClose: () => void;
  tab: RightPanelTab;
}) {
  const [showCollapseBtn, setShowCollapseBtn] = useState(false);
  const translate = useTranslate();
  const record = useRecordContext();

  const canAccessLogs = useCanAccess({
    resource: `logs/${logs?.resource ?? 'task'}`,
    action: 'list',
  });
  const canAccessComments = useCanAccess({
    resource: 'bo_comments',
    action: 'list',
  });

  if (!record) {
    return null;
  }

  let logsTargetId = record.id;
  if (logs && typeof logs.targetId === 'function') {
    logsTargetId = logs.targetId(record);
  } else if (logs && typeof logs.targetId === 'number') {
    logsTargetId = logs.targetId;
  }

  const effectiveCommentsTarget =
    comments && (isFunction(comments.target) ? comments.target(record) : comments.target);

  const commentsFilteringValue = comments?.getFilterValue
    ? comments.getFilterValue(record[comments.source])
    : record[comments?.source ?? 'id'];

  return (
    <Card
      sx={{
        position: 'fixed',
        top: '15px',
        right: '10px',
        width: '400px',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'visible',
      }}
      onMouseOver={() => setShowCollapseBtn(true)}
      onMouseOut={() => setShowCollapseBtn(false)}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          '& .RaTimelineGroup-label': {
            color: '#999999',
            marginLeft: '15px',
            fontSize: 'small',
          },
          '& .RaTimelineGroup-events': {
            borderLeft: '1px solid #CCCCCC',
            marginLeft: '17px',
          },
        }}>
        <TabbedShowLayout
          syncWithLocation={false}
          sx={{
            '& .RaTabbedShowLayout-content': {
              height: 'calc(100vh - 70px) !important',
              overflowY: 'scroll',
            },
          }}
          tabs={<Tabs tab={tab} comments={comments} />}>
          {comments && canAccessComments && (
            <Tab
              sx={{ flexDirection: 'row', width: logs ? '50%' : '100%' }}
              label={
                <>
                  {translate('general.comments.label')}
                  <Chip
                    size="small"
                    sx={{
                      height: '20px',
                      marginLeft: '5px',
                      backgroundColor: '#F8F8F8',
                      '& .MuiChip-label': {
                        padding: 0,
                      },
                    }}
                    label={
                      <Count
                        sx={{
                          fontWeight: 600,
                        }}
                        resource="bo_comments"
                        filter={{ [effectiveCommentsTarget as string]: commentsFilteringValue }}
                      />
                    }
                  />
                </>
              }
              path="comments">
              <>
                 
                {effectiveCommentsTarget && (
                  <CommentsPanel {...comments} target={effectiveCommentsTarget} />
                )}
              </>
            </Tab>
          )}
          {logs && canAccessLogs && (
            <Tab
              sx={{ flexDirection: 'row', width: comments ? '50%' : '100%' }}
              label={
                <Box>
                  {translate('general.logs.label')}
                  <Chip
                    size="small"
                    sx={{
                      height: '20px',
                      marginLeft: '5px',
                      backgroundColor: '#F8F8F8',
                      '& .MuiChip-label': {
                        padding: 0,
                      },
                    }}
                    label={
                      <Count
                        sx={{
                          fontWeight: 600,
                        }}
                        resource={`logs/${logs.resource}`}
                        filter={{
                          [BOLogIdFieldMap[logs.resource]]: logsTargetId,
                          ...logs.filter,
                        }}
                      />
                    }
                  />
                </Box>
              }
              path="logs">
              <LogsPanel {...logs} />
            </Tab>
          )}
        </TabbedShowLayout>
      </Box>
      {showCollapseBtn && (
        <Fab
          size="small"
          sx={{
            position: 'absolute',
            top: '40%',
            left: '-20px',
          }}
          onClick={onClose}>
          <KeyboardArrowRightIcon />
        </Fab>
      )}
    </Card>
  );
}
